/**
 * The Rent sheet's map — `docs/visit-page.md` §10.2, Board 6 B: the yard, the
 * vendor and the site with the route between them (*9 min from the yard · 18
 * min on to the site* is under it, folder 48's estimate — never Routes, never
 * stored).
 *
 * New request's day map (`requests/schedule/day-map.tsx`) is the pattern and
 * the reason for each choice here: MapLibre over the Google tiles base the
 * Property maps use (`imagerySession`), **arriving by dynamic import** so a
 * sheet nobody opens pays nothing; a picture fitted to its three places, never
 * panned or zoomed under a hand; the plain base when there are no tiles —
 * the places and the route are the information, the tiles the backdrop.
 *
 * The markers are Board 6 B's: the yard a 22px ink tile with the house, the
 * vendor a white ring in the rental amber with its initial, the site the green
 * pin with its halo and a white label; the route the indigo dash.
 */
import { useEffect, useRef, useState } from 'react'
import type { Map as MapLibreMap, Marker } from 'maplibre-gl'
import { metresPerPixel, scaleBar, type LatLng } from '@rowted/shared'
import { tokenColour } from '../lib/token-colour.js'
import { imagerySession, type ImagerySession } from '../lib/map-provider.js'

export interface RentMapPlaces {
  yard: LatLng
  vendor: LatLng | null
  site: LatLng
  labels: { yard: string; site: string; vendor: string }
}

function element(className: string, ...children: (Element | string)[]): HTMLElement {
  const node = document.createElement('div')
  node.className = className
  node.append(...children)
  return node
}

/** An SVG element from camel-cased attributes, so no markup string sits in the file. */
function svg(tag: string, attributes: Record<string, string>, ...children: Element[]): SVGElement {
  const node = document.createElementNS('http://www.w3.org/2000/svg', tag)
  for (const [name, value] of Object.entries(attributes)) {
    node.setAttribute(name === 'viewBox' ? name : name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`), value)
  }
  node.append(...children)
  return node
}

function label(text: string, className = 'text-[11px] font-semibold text-text-body'): HTMLElement {
  const node = document.createElement('span')
  node.className = `whitespace-nowrap ${className} [text-shadow:0_0_3px_rgb(var(--color-surface))]`
  node.textContent = text
  return node
}

export function RentMap({ places, height = 150 }: { places: RentMapPlaces; height?: number }) {
  const host = useRef<HTMLDivElement>(null)
  const map = useRef<MapLibreMap | null>(null)
  const markers = useRef<Marker[]>([])
  const [session, setSession] = useState<ImagerySession | null | undefined>(undefined)
  const [ready, setReady] = useState(false)
  const [scale, setScale] = useState<{ label: string; pixels: number } | null>(null)

  useEffect(() => {
    let live = true
    imagerySession()
      .then((got) => { if (live) setSession(got) })
      .catch(() => { if (live) setSession(null) })
    return () => { live = false }
  }, [])

  useEffect(() => {
    if (session === undefined || !host.current || map.current) return undefined
    let cancelled = false
    let made: MapLibreMap | null = null
    void (async () => {
      const maplibre = await import('maplibre-gl')
      await import('maplibre-gl/dist/maplibre-gl.css')
      maplibre.setWorkerUrl((await import('maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url')).default)
      if (cancelled || !host.current) return
      made = new maplibre.Map({
        container: host.current,
        style: session
          ? {
            version: 8,
            sources: { base: { type: 'raster', tiles: session.tiles.map, tileSize: 256, maxzoom: Math.min(21, session.maxZoom) } },
            layers: [{ id: 'base', type: 'raster', source: 'base' }],
          }
          : {
            version: 8,
            sources: {},
            layers: [{ id: 'plain', type: 'background', paint: { 'background-color': tokenColour('surface-group') } }],
          },
        center: [places.site.lng, places.site.lat],
        zoom: 11,
        interactive: false,
        attributionControl: false,
        renderWorldCopies: false,
        pixelRatio: Math.min(2, window.devicePixelRatio || 1),
      })
      map.current = made
      made.on('load', () => { if (!cancelled) setReady(true) })
    })()
    return () => {
      cancelled = true
      for (const marker of markers.current) marker.remove()
      markers.current = []
      made?.remove()
      if (map.current === made) map.current = null
      setReady(false)
    }
    // The map is made once per base; the places move it below.
  }, [session])

  useEffect(() => {
    const current = map.current
    if (!ready || !current) return
    let cancelled = false
    void (async () => {
      const maplibre = await import('maplibre-gl')
      if (cancelled) return
      for (const marker of markers.current) marker.remove()
      markers.current = []

      const route = [places.yard, ...(places.vendor ? [places.vendor] : []), places.site]
      const line = {
        type: 'Feature' as const,
        properties: {},
        geometry: { type: 'LineString' as const, coordinates: route.map((point) => [point.lng, point.lat]) },
      }
      const source = current.getSource('route')
      if (source && 'setData' in source) {
        (source as { setData: (data: unknown) => void }).setData(line)
      } else {
        current.addSource('route', { type: 'geojson', data: line })
        current.addLayer({
          id: 'route', type: 'line', source: 'route',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': tokenColour('indigo'), 'line-opacity': 0.55, 'line-width': 3, 'line-dasharray': [2, 1.7] },
        })
      }

      const bounds = new maplibre.LngLatBounds()
      for (const point of route) bounds.extend([point.lng, point.lat])
      current.fitBounds(bounds, { padding: { top: 30, bottom: 30, left: 40, right: 150 }, maxZoom: 15, duration: 0 })

      const add = (point: LatLng, node: HTMLElement, anchor: 'left' | 'bottom-left') => {
        markers.current.push(new maplibre.Marker({ element: node, anchor }).setLngLat([point.lng, point.lat]).addTo(current))
      }

      /* The yard: the ink tile with the house, its name beside it. */
      const house = svg('svg', { viewBox: '0 0 22 22', width: '22', height: '22', fill: 'none', stroke: 'currentColor', strokeWidth: '1.6' },
        svg('path', { d: 'M5 12l6-6l6 6M7 11v6h8v-6' }))
      add(places.yard, element('flex -translate-x-[11px] items-center gap-1',
        element('flex h-[22px] w-[22px] items-center justify-center rounded-[6px] bg-toast text-toast-text', house),
        label(places.labels.yard)), 'left')

      /* The vendor: a white ring in the rental amber with its initial. */
      if (places.vendor) {
        const ring = element('flex h-[22px] w-[22px] items-center justify-center rounded-full border-[2.5px] bg-surface text-[11px] font-bold')
        ring.style.borderColor = 'rgb(var(--status-kind-rental-fg))'
        ring.style.color = 'rgb(var(--status-kind-rental-fg))'
        ring.textContent = places.labels.vendor.charAt(0)
        add(places.vendor, element('flex -translate-x-[11px] items-center gap-1', ring, label(places.labels.vendor)), 'left')
      }

      /* The site: the green pin over its halo, and the white label. */
      const pin = svg('svg', { viewBox: '0 0 24 30', width: '22', height: '28', class: 'relative' },
        svg('path', {
          d: 'M12 29C3 17 1 12 1 8.5A11 11 0 1 1 23 8.5C23 12 21 17 12 29z',
          fill: 'currentColor', stroke: 'rgb(var(--color-surface))', strokeWidth: '2',
        }),
        svg('circle', { cx: '12', cy: '9', r: '4', fill: 'rgb(var(--color-surface))' }))
      const halo = element('absolute left-1/2 top-[9px] h-11 w-11 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent/[.12]')
      const site = element('relative flex -translate-x-[11px] items-start gap-1.5 text-accent', halo, pin,
        label(places.labels.site, 'mt-[-6px] rounded-[6px] border border-accent-line bg-surface px-[7px] py-[3px] text-[11.5px] font-bold text-accent-dark'))
      add(places.site, site, 'bottom-left')

      const centre = current.getCenter()
      setScale(scaleBar(metresPerPixel(centre.lat, current.getZoom()), 44))
    })()
    return () => { cancelled = true }
  }, [ready, places])

  return (
    <div className="relative overflow-hidden bg-surface-group" style={{ height }} data-rent-map>
      <div ref={host} className="absolute inset-0 h-full w-full" />
      {scale && (
        <div className="absolute bottom-2 right-2.5 flex items-center gap-1.5 text-[10px] text-text-muted" aria-hidden>
          <span className="block h-[7px] border-x-2 border-b-2 border-text-muted" style={{ width: scale.pixels }} />
          {scale.label}
        </div>
      )}
      {session && (
        <div className="absolute bottom-0.5 left-1.5 max-w-[60%] truncate text-[8.5px] text-text-faint">{session.attribution}</div>
      )}
    </div>
  )
}
